import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// import components
import AnnouncementBar from "./AnnouncementBar";
import MobileMenu from "./MobileMenu";
import DropdownMenu from "./DropdownMenu";

// import logo
import logo from "../../assets/logo.png";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkClick = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Toast Notifications */}
      <ToastContainer position="top-right" autoClose={3000} />

      <AnnouncementBar />

      {/* Main Header */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-100 shadow-sm">
        <div className="mx-auto flex max-w-screen-xl items-center justify-between px-4 py-2 md:px-8">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Link to="/" onClick={handleLinkClick}>
              <img src={logo} alt="Logo" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav
            ref={navRef}
            className="hidden lg:flex items-center space-x-6 text-sm font-medium text-gray-800 ml-13"
          >
            {/** Services Dropdown */}
            <DropdownMenu
              title="Services"
              items={[
                { to: "/services/ai-services/", label: "AI Services" },
                { to: "/services/web-development/", label: "Web Development" },
                {
                  to: "/services/mobile-app-development/",
                  label: "Mobile App Development",
                },
                {
                  to: "/services/software-consulting/",
                  label: "Software Consulting",
                },
                {
                  to: "/services/third-party-integration/",
                  label: "Third Party Integration",
                },
                {
                  to: "/services/custom-development/",
                  label: "Custom Development",
                },
                {
                  to: "/services/enterprise-application/",
                  label: "Enterprise Application",
                },
                {
                  to: "/services/digital-transformation/",
                  label: "Digital Transformation",
                },
              ]}
              openDropdown={openDropdown}
              toggleDropdown={toggleDropdown}
              handleLinkClick={handleLinkClick}
            />

            {/** Industries Dropdown */}
            <DropdownMenu
              title="Industries"
              items={[
                { to: "/industries/logistics/", label: "Logistics" },
                { to: "/industries/healthcare/", label: "Healthcare" },
                {
                  to: "/industries/financial-services/",
                  label: "Financial Services",
                },
                { to: "/industries/education/", label: "Education" },
                { to: "/industries/hospitality/", label: "Hospitality" },
                { to: "/industries/entertainment/", label: "Entertainment" },
                { to: "/industries/energy/", label: "Energy" },
                { to: "/industries/warehouse/", label: "Warehouse" },
                { to: "/industries/manufacture/", label: "Manufacture" },
                { to: "/industries/ecommerce/", label: "E-commerce" },
              ]}
              openDropdown={openDropdown}
              toggleDropdown={toggleDropdown}
              handleLinkClick={handleLinkClick}
            />

            {/** Company Dropdown */}
            <DropdownMenu
              title="Company"
              items={[
                { to: "/company/our-advantages/", label: "Our advantages" },
                { to: "/company/our-story/", label: "Our Story" },
                {
                  to: "/company/people-of-lentera/",
                  label: "People of Lentera",
                },
                { to: "/company/our-brand/", label: "Our Brand" },
                { to: "/company/careers/", label: "Careers" },
                { to: "/blogs", label: "Blogs", external: true },
                // { to: "https://www.lentera.in/contact-us/", label: "Support us", external: true },
              ]}
              openDropdown={openDropdown}
              toggleDropdown={toggleDropdown}
              handleLinkClick={handleLinkClick}
            />

            {/* Contact Link */}
            <Link
              to="/contact/"
              onClick={handleLinkClick}
              className="text-black hover:text-black"
            >
              Contact us
            </Link>
          </nav>

          {/* Book a Free Consultation - Desktop */}
          <a
            href="https://outlook.office365.com/book/FreeConsultation@lenteratechno.onmicrosoft.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block w-[240px] h-[44px] rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 text-center leading-[28px]"
          >
            Book a Free Consultation
          </a>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden flex items-center"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <svg
              className="h-6 w-6 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={
                  isMobileMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <MobileMenu handleLinkClick={handleLinkClick} />}
      </header>
    </>
  );
};

export default Header;
